import {
  getCookieConsentGiven,
  subscribeCookieConsent,
  toCookieInformationKey,
  type CookieConsentCategory,
} from '@pkg/ui/composables/useCookieConsent';

declare global {
  interface Window {
    CookieInformation: any;
  }

  interface Navigator {
    globalPrivacyControl: boolean | undefined;
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const { $updateAnalyticsConsent } = nuxtApp as {
    $updateAnalyticsConsent?: (isConsentGiven: boolean) => void;
  };

  // Same CookieInformation events as useCookieConsent — one shared listener registry.
  subscribeCookieConsent(() => {
    const marketing = getCookieConsentGiven('marketing');
    const statistic = getCookieConsentGiven('statistic');

    // Preserve “no decision yet” (null/undefined): only push analytics when explicit.
    if (marketing === true && statistic === true) {
      $updateAnalyticsConsent?.(true);
    } else if (marketing === false || statistic === false) {
      $updateAnalyticsConsent?.(false);
    }
  });

  // Hide cookie consent banner on cookie-policy pages.
  // Users should be able to read the cookie policy without being forced
  // to accept or reject the banner. On other pages it shows as usual.
  const router = useRouter();
  const STYLE_ID = 'hide-cookie-banner-style';

  const isCookiePolicyPage = (path: string): boolean => {
    return /\/legal\/cookie-policy/i.test(path) || /\/legal\/privacy\/?$/i.test(path);
  };

  // Store references so we can remove them when navigating away
  let forceScrollWheel: ((e: Event) => void) | null = null;
  let forceScrollTouch: ((e: Event) => void) | null = null;

  const hideBanner = () => {
    if (document.getElementById(STYLE_ID)) { return; }
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #coiOverlay,
      #coi-banner-wrapper,
      .coi-banner__wrapper,
      .coi-consent-banner,
      [data-coi-banner],
      #CybotCookiebotDialog,
      .CookieInformationDialog {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    // Unlock scroll — the SDK locks it via JS event listeners
    forceScrollWheel = (e: Event) => { e.stopPropagation(); };
    forceScrollTouch = (e: Event) => { e.stopPropagation(); };
    window.addEventListener('wheel', forceScrollWheel, { capture: true });
    window.addEventListener('touchmove', forceScrollTouch, { capture: true });
    document.documentElement.style.setProperty('overflow', 'auto', 'important');
    document.body.style.setProperty('overflow', 'auto', 'important');
  };

  const showBanner = () => {
    document.getElementById(STYLE_ID)?.remove();

    // Clean up scroll-unlock listeners
    if (forceScrollWheel) {
      window.removeEventListener('wheel', forceScrollWheel, { capture: true });
      forceScrollWheel = null;
    }
    if (forceScrollTouch) {
      window.removeEventListener('touchmove', forceScrollTouch, { capture: true });
      forceScrollTouch = null;
    }
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
  };

  router.afterEach((to) => {
    if (isCookiePolicyPage(to.path)) {
      hideBanner();
    } else {
      showBanner();
    }
  });

  if (isCookiePolicyPage(window.location.pathname)) {
    hideBanner();
  }
});

const gpcCheck = () => {
  const marketingKey = toCookieInformationKey('marketing' satisfies CookieConsentCategory);
  if (
    navigator.globalPrivacyControl &&
    window.CookieInformation &&
    window.CookieInformation.getConsentGivenFor(marketingKey) === true
  ) {
    window.CookieInformation.changeCategoryConsentDecision(marketingKey);
  }
};

gpcCheck();
